import { useEffect, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { ISerologyFinalXmBloodDraw } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ISerologyFinalXmBloodDraw[],
    mode: 'serology' | 'finalxm',
    onViewResults: (row: ISerologyFinalXmBloodDraw) => void
}

const Grid = (props: IProps) => {
    const gridApiRef = useRef<GridApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    useEffect(() => {
        let timer = setTimeout(() => {
            gridApiRef.current?.sizeColumnsToFit();
        }, 0);

        return () => clearTimeout(timer);
    }, [props.mode]);

    let columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 50,
            sortable: false
        },
        {
            colId: 'donorCenterAlias',
            field: 'donorCenterAlias',
            headerName: 'Donor Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 90,
            sortable: true
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 110,
            sortable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyFinalXmBloodDraw;

                return <a href={`/center/${data.donorCenterId}/donor/${data.donorId}/profile/donor-data`} target='_blank'>{data.donorAlias}</a>;
            }
        },
        {
            colId: 'bloodDrawDate',
            field: 'bloodDrawDate',
            headerName: 'Blood Draw Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Blood Draw<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 110,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'uploadDate',
            headerName: 'Upload Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Upload<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 105,
            sortable: true,
            comparator: Utility.dateComparator,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ISerologyFinalXmBloodDraw;

                return props.mode == 'serology' ? data.serologyUploadDate : data.finalXmUploadDate;
            }
        },
        {
            colId: 'donorSurgeryDate',
            field: 'donorSurgeryDate',
            headerName: 'Donor Surgery Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Surgery<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 115,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'recipientSurgeryDate',
            field: 'recipientSurgeryDate',
            headerName: 'Recipient Surgery Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Surgery<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'deltaDays',
            field: 'deltaDays',
            headerName: 'Delta (Days)',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Delta<br />(Days)</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyFinalXmBloodDraw;

                return <>{data.deltaDays != null ? data.deltaDays : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 90,
            sortable: true
        },
        {
            colId: 'viewResults',
            headerName: '',
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 125,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyFinalXmBloodDraw;
                let hasResults = props.mode == 'serology' ? !!data.serologyUploadDate : !!data.finalXmId;

                return <Button bsSize='xsmall' disabled={!hasResults} onClick={() => props.onViewResults(data)}>View Results</Button>;
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [props.mode]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No blood draw records found for the selected month...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onGridSizeChanged={onGridSizeChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;
